/**
 * <LiveTutorAbsentDialog> — le tuteur n'est pas venu : la séance de remplacement, ou le rattrapage
 * (template).
 *
 * À dix minutes sans tuteur, le serveur cherche un live qui commence à la même heure
 * (`tutorNoShow/`). Ce qui se dit ensuite, étape par étape, avec les mots d'aujourd'hui :
 *  - `proposal` : « Ton masteur n'a pas pu venir », le live trouvé (sa ligne de séance,
 *    `<SessionRow>`), « Rejoindre ma séance ». Rattraper plus tard n'est pas un bouton égal : la
 *    croix y mène, comme aujourd'hui ;
 *  - `confirmJoin` : « Confirme ta nouvelle séance », « OK, j'y vais » ;
 *  - `confirmLater` : « Rattraper plus tard », le mail et les parents prévenus, « Confirmer et
 *    quitter » ;
 *  - `credited` : le rattrapage crédité, que l'élève ait préféré attendre (`declined`) ou qu'aucun
 *    live ne soit libre (`none`). Elle ne se ferme pas autrement que par son bouton.
 *
 * Les champions ne parlent pas dans une boîte (le livre) : Abou n'y est plus. Contrôlée : l'étape,
 * le live trouvé et l'envoi arrivent en props.
 */
import React from "react"

import { type UILiveSession } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { CardList } from "../../molecules/CardList"
import { Modal } from "../../molecules/Modal"
import { SessionRow } from "../../molecules/SessionRow"

export type LiveTutorAbsentStep = "proposal" | "confirmJoin" | "confirmLater" | "credited"

/** Pourquoi le rattrapage est crédité : l'élève a préféré attendre, ou aucun live n'était libre. */
export type LiveCreditReason = "declined" | "none"

export type LiveTutorAbsentDialogProps = {
    visible: boolean
    step: LiveTutorAbsentStep
    /** Le live trouvé, à l'étape `proposal`. */
    replacement?: UILiveSession | null
    /** À l'étape `credited`. Défaut `none`. */
    creditReason?: LiveCreditReason
    /** « Rejoindre ma séance ». */
    onJoin: () => void
    /** La croix de la proposition : rattraper plus tard. */
    onDecline: () => void
    /** « OK, j'y vais ». */
    onConfirmJoin: () => void
    /** « Confirmer et quitter », sur `confirmLater` comme sur `credited`. */
    onConfirmLater: () => void
    /** « Retour » : à la proposition. */
    onBack: () => void
    /** L'élève part dans l'autre live : « OK, j'y vais » tourne. Défaut `false`. */
    joining?: boolean
    testID?: string
}

/** Une étape seule : le titre passe à la ligne, la croix à droite, sans barre de progression. */
const ONE_STEP = { step: 1, total: 1 }

const TEXTS = {
    proposal: {
        title: "Ton masteur n'a pas pu venir",
        message: "Un autre masteur t'attend dans un live, à la même heure : tu ne perds pas ta séance.",
        join: "Rejoindre ma séance",
    },
    confirmJoin: {
        title: "Confirme ta nouvelle séance",
        message: "Tu rejoins un autre live, tout de suite.",
        confirm: "OK, j'y vais",
    },
    confirmLater: {
        title: "Rattraper plus tard",
        message: "Tu recevras un mail pour réserver ton rattrapage. Tes parents seront prévenus.",
        confirm: "Confirmer et quitter",
    },
    credited: {
        title: { declined: "Tu préfères rattraper plus tard", none: "Ton masteur est absent" },
        message: {
            declined: "On te crédite un rattrapage et on prévient tes parents.",
            none: "Aucun live libre aujourd'hui. On te crédite un rattrapage et on prévient tes parents.",
        },
        cardTitle: "Séance de rattrapage créditée",
        cardMessage: "Réserve-le depuis le mail qu'on t'a envoyé.",
        confirm: "Confirmer et quitter",
    },
    parents: "Parles-en avec tes parents avant de confirmer.",
    back: "Retour",
}

export function LiveTutorAbsentDialog(props: LiveTutorAbsentDialogProps) {
    const {
        visible,
        step,
        replacement,
        creditReason = "none",
        onJoin,
        onDecline,
        onConfirmJoin,
        onConfirmLater,
        onBack,
        joining = false,
        testID,
    } = props

    if (step === "proposal") {
        return (
            <Modal
                visible={visible}
                onClose={onDecline}
                title={TEXTS.proposal.title}
                fitContent
                steps={ONE_STEP}
                footer={<Button label={TEXTS.proposal.join} color="success" onPress={onJoin} fullWidth />}
                testID={testID}
            >
                <Stack gap="4">
                    <Text size="text-md">{TEXTS.proposal.message}</Text>
                    {replacement != null && (
                        <CardList>
                            <SessionRow session={replacement} relativeDay />
                        </CardList>
                    )}
                </Stack>
            </Modal>
        )
    }

    if (step === "credited") {
        return (
            <Modal
                visible={visible}
                onClose={onConfirmLater}
                title={TEXTS.credited.title[creditReason]}
                fitContent
                steps={ONE_STEP}
                dismissable={false}
                footer={<Button label={TEXTS.credited.confirm} onPress={onConfirmLater} fullWidth />}
                testID={testID}
            >
                <Stack gap="4">
                    <Text size="text-md">{TEXTS.credited.message[creditReason]}</Text>
                    <AlertCard severity="info" title={TEXTS.credited.cardTitle} message={TEXTS.credited.cardMessage} />
                </Stack>
            </Modal>
        )
    }

    const confirm = step === "confirmJoin" ? TEXTS.confirmJoin : TEXTS.confirmLater
    const onConfirm = step === "confirmJoin" ? onConfirmJoin : onConfirmLater

    return (
        <Modal
            visible={visible}
            onClose={onBack}
            title={confirm.title}
            fitContent
            steps={ONE_STEP}
            footer={
                <Stack gap="2">
                    <Button
                        label={confirm.confirm}
                        color={step === "confirmJoin" ? "success" : undefined}
                        onPress={onConfirm}
                        loading={step === "confirmJoin" && joining}
                        fullWidth
                    />
                    <Button label={TEXTS.back} variant="ghost" onPress={onBack} fullWidth />
                </Stack>
            }
            testID={testID}
        >
            <Stack gap="4">
                <Text size="text-md">{confirm.message}</Text>
                <AlertCard severity="warning" message={TEXTS.parents} />
            </Stack>
        </Modal>
    )
}
